import { useState, useEffect } from "react";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import {
  Container,
  Grid,
  LinearProgress,
  Card,
  CardContent,
  Typography,
  Switch,
  OutlinedInput,
  Button,
} from "@material-ui/core";

import HeaderClientes from "components/_refactor/headers/clients.js";
import Accordion from "components/_refactor/accordion";
import Api from "lib/api/httpRequestHelper";
import useLogs from "hooks/logs";
import StructureFields from "components/_refactor/fields/structure";

const SYSTEM_CONFIGS = [
  { id: "karstenkonecta", label: "Karsten Konecta" },
  { id: "omnichat", label: "OmniChat" },
  { id: "seta", label: "Seta" },
];

const SHIPPING_CONFIGS = [
  {
    id: "prateleira",
    label: "Prateleira Infinita",
    description: "Venda produtos do e-commerce diretamente na loja.",
    child: [
      { id: "prazoExtraFrete", label: "Prazo extra no frete", type: "number" },
      {
        id: "margemSegurancaEstoque",
        label: "Margem de Segurança Estoque",
        type: "number",
      },
      { id: "percentualMarkup", label: "Percentual de Markup", type: "number" },
      {
        id: "tempoEstorno",
        label: "Tempo para estorno da reserva de estoque",
        type: "number",
      },
      {
        id: "codCentroCusto",
        label: "Centro de custo de rateio",
        type: "number",
      },
    ],
  },
  {
    id: "entregueloja",
    label: "Retire em Loja",
    description: "Permitir que o cliente compre online e retire na loja.",
    child: [
      { id: "prazoExtraFrete", label: "Prazo extra no frete", type: "number" },
    ],
  },
  {
    id: "retireloja",
    label: "Entrega pela Loja",
    description: "Permitir que a loja faça a entrega local.",
  },
];

const PAYMENT_CONFIGS = [
  {
    id: "automaticPayment",
    label: "Faturamento Automático",
    child: [
      { id: "caixaTerminal", label: "Usuário Caixa Terminal", type: "number" },
    ],
  },
  {
    id: "linkPagamento",
    label: "Link de Pagamento",
    child: [
      { id: "ec", label: "EC", type: "number" },
      { id: "clientSecret", label: "Client Secret", type: "text" },
      { id: "clientId", label: "Client Id", type: "text" },
    ],
  },
  {
    id: "pix",
    label: "PIX",
    child: [
      { id: "key", label: "Key", type: "text" },
      { id: "secret", label: "Secret", type: "text" },
      { id: "expirationTime", label: "Tempo de Expiração", type: "number" },
    ],
  },
  {
    id: "tef",
    label: "TEF",
    child: [
      { id: "user", label: "Usuário", type: "text" },
      { id: "password", label: "Senha", type: "text" },
    ],
  },
];

export default function ConfiguracoesOmni() {
  const snackbar = useSnackbar();
  const { setLocalStorageActions } = useLogs();

  const [structureFilters, setStructureFilters] = useState({});
  const [structureFiltersLoading, setStructureFiltersLoading] = useState(false);

  const [searchingParameterList, setSearchingParameterList] = useState(false);
  const [parametersPI, setParametersPI] = useState({});
  const [parametersRL, setParametersRL] = useState({});
  const [vendaLink, setVendaLink] = useState({});

  const [sistemConfig, setSistemConfig] = useState({
    karstenkonecta: false,
    omnichat: false,
    seta: false,
  });

  const [shippingConfig, setShippingConfig] = useState({
    prateleira: {
      active: false,
      prazoExtraFrete: 0,
      margemSegurancaEstoque: 0,
      percentualMarkup: 0,
      tempoEstorno: 0,
      codCentroCusto: "",
    },
    entregueloja: {
      active: false,
      prazoExtraFrete: 0,
    },
    retireloja: {
      active: false,
    },
  });

  const [paymentConfig, setPaymentConfig] = useState({
    automaticPayment: {
      active: false,
      caixaTerminal: 0,
    },
    linkPagamento: {
      active: false,
      ec: 0,
      clientSecret: 0,
      clientId: 0,
    },
    pix: {
      active: false,
      key: "",
      secret: "",
      expirationTime: 0,
    },
    tef: {
      active: false,
      user: "",
      password: "",
    },
  });

  const [updatingConfigs, setUpdatingConfigs] = useState(false);

  const handleGetParameters = async () => {
    setSearchingParameterList(true);

    try {
      const { Success, Result } = await Api.get(
        "parameter-list",
        "configuracoesOmni",
        "?idPessoaLoja=" + structureFilters.idLoja + "&idMarca=1",
        true
      );
      if (Success) {
        Result?.map((modalidades) => {
          if (modalidades?.Modalidade === "PI") {
            setParametersPI(modalidades);
          }
          if (modalidades?.Modalidade === "RL") {
            setParametersRL(modalidades);
          }
        });
      }

      const { Success: Success2, Result: Result2 } = await Api.get(
        "list-config-client",
        "configuracoesOmniVendaLink",
        "?idPessoaLoja=" + structureFilters.idLoja,
        true
      );
      if (Success2) {
        setVendaLink(
          Array.isArray(Result2)
            ? Result2.find(
                (item) =>
                  item.IdPessoaLoja?.toString() ===
                  structureFilters.idLoja?.toString()
              )
            : {}
        );
      }
    } catch (error) {
      snackbar.showMessage(error, {
        severity: "error",
      });
    } finally {
      setSearchingParameterList(false);
    }
  };

  const handleUpdateVendaLink = async () => {
    const object = {
      idPessoaLoja: structureFilters.idLoja.toString(),
      clientId: vendaLink.ClientId?.toString(),
      clientSecret: vendaLink.ClientSecret?.toString(),
      EC: vendaLink.Ec?.toString(),
      ...(vendaLink?.IdVendaLinkConfiguracao && {
        idConfiguracaoLink: vendaLink.IdVendaLinkConfiguracao?.toString(),
      }),
    };

    try {
      await Api.put(
        vendaLink?.IdVendaLinkConfiguracao
          ? "edit-config-client"
          : "create-config-client",
        "configuracoesOmniVendaLink",
        object,
        true
      );
    } catch (error) {
      snackbar.showMessage(error, {
        severity: "error",
      });
    }
  };

  const handleUpdateConfigs = async () => {
    setUpdatingConfigs(true);

    const object = {
      idMarca: "1",
      idCanal: parametersPI?.IdCanal?.toString(),
      idPessoaRepresentante: parametersPI?.IdPessoaRepresentante?.toString(),
      idGrupoEconomico: parametersPI?.IdGrupoEconomico?.toString() || "0",
      idPessoaLoja: structureFilters.idLoja?.toString() || "0",
      idEmpresaSeta: structureFilters.idLoja?.IdFilialEmpresa?.toString(),

      sistema: Object.keys(sistemConfig)
        .filter((key) => sistemConfig[key])
        .map((key) => key.toUpperCase())
        .join(","),

      formaEntrega: Object.keys(shippingConfig)
        .filter((key) => shippingConfig[key].active)
        .map((key) => key.toUpperCase())
        .join(","),

      prazoExtraFretePi: shippingConfig.prateleira.prazoExtraFrete?.toString(),
      margemSegurancaEstoquePi:
        shippingConfig.prateleira.margemSegurancaEstoque?.toString(),
      percentualMarkupPi:
        shippingConfig.prateleira.percentualMarkup?.toString(),
      tempoEstornoEstoquePi: shippingConfig.prateleira.tempoEstorno?.toString(),
      cCustoRateioPi: shippingConfig.prateleira.codCentroCusto?.toString(),
      prazoExtraFreteRl:
        shippingConfig.entregueloja.prazoExtraFrete?.toString(),

      formaPagamento: Object.keys(paymentConfig)
        .filter((key) => paymentConfig[key].active)
        .map((key) => key.toUpperCase())
        .join(","),
      key: paymentConfig.pix.key?.toString(),
      secret: paymentConfig.pix.secret?.toString(),
      timePix: paymentConfig.pix.expirationTime?.toString(),
      habilitarFaturamentoPi: paymentConfig.automaticPayment.active,
      terminalCaixaPi: paymentConfig.automaticPayment.caixaTerminal?.toString(),
      formaPagamentoTefUser: paymentConfig.tef.user?.toString(),
      formaPagamentoTefSenha: paymentConfig.tef.password?.toString(),
    };

    try {
      const { Success } = await Api.put(
        "edit-parameter",
        "configuracoesOmni",
        object,
        true
      );
      if (Success) {
        await handleUpdateVendaLink();
        snackbar.showMessage("Alterações salvas com sucesso!", {
          severity: "success",
        });
      }
    } catch (error) {
      snackbar.showMessage(error, {
        severity: "error",
      });
    } finally {
      setUpdatingConfigs(false);
    }
  };

  useEffect(() => {
    setLocalStorageActions({
      nomeTela: "Configurações Omni",
      idMenu: "152",
      acao: "Acessou o menu Configurações Omni",
    });
  }, []);

  useEffect(() => {
    setSistemConfig({
      karstenkonecta:
        parametersPI?.FormaSistemaReturn?.includes("KARSTENKONECTA") || false,
      omnichat: parametersPI?.FormaSistemaReturn?.includes("OMNICHAT") || false,
      seta: parametersPI?.FormaSistemaReturn?.includes("SETA") || false,
    });

    setShippingConfig({
      prateleira: {
        active:
          parametersPI?.FormaEntregaReturn?.includes("PRATELEIRA") || false,
        prazoExtraFrete: parametersPI?.PrazoExtraFretePi || 0,
        margemSegurancaEstoque: parametersPI?.MargemSegurancaEstoquePi || 0,
        percentualMarkup: parametersPI?.PercentualMarkupPi || 0,
        tempoEstorno: parametersPI?.TempoEstornoEstoquePi || 0,
        codCentroCusto: parametersPI?.CCustoRateioPi || "",
      },
      entregueloja: {
        active:
          parametersPI?.FormaEntregaReturn?.includes("ENTREGUELOJA") || false,
        prazoExtraFrete: parametersRL?.PrazoExtraFreteRl || 0,
      },
      retireloja: {
        active:
          parametersPI?.FormaEntregaReturn?.includes("RETIRELOJA") || false,
      },
    });

    setPaymentConfig({
      automaticPayment: {
        active: parametersPI?.HabilitarFaturamentoPi || false,
        caixaTerminal: parametersPI?.TerminalCaixaPi || 0,
      },
      linkPagamento: {
        active:
          parametersPI?.FormaPagamentoReturn?.includes("LINKPAGAMENTO") ||
          false,
        ec: vendaLink.Ec,
        clientSecret: vendaLink.ClientSecret,
        clientId: vendaLink.ClientId,
      },
      pix: {
        active: parametersPI?.FormaPagamentoReturn?.includes("PIX") || false,
        key: parametersPI.Key || "",
        secret: parametersPI.Secret || "",
        expirationTime: parametersPI.Timepix || 0,
      },
      tef: {
        active: parametersPI?.FormaPagamentoReturn?.includes("TEF") || false,
        user: parametersPI?.FormaPagamentoTefUser || "",
        password: parametersPI?.FormaPagamentoTefSenha || "",
      },
    });
  }, [parametersPI, parametersRL, vendaLink]);

  useEffect(() => {
    if (!structureFilters.idLoja) return;
    handleGetParameters();
  }, [structureFilters.idLoja]);

  return (
    <>
      <HeaderClientes section="Configurações Omni" subsection="Administração" />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <StructureFields
                onStructureFieldsChange={setStructureFilters}
                disableAll={searchingParameterList}
                onStructureFieldsLoad={setStructureFiltersLoading}
                requiredFields={["economicGroup", "store"]}
                hideFields={["brand", "channel", "representative"]}
                showSingleStore={false}
              />
            </Grid>
          </CardContent>
        </Card>

        {searchingParameterList && (
          <Grid item xs={12}>
            <span>Buscando configurações...</span>
            <LinearProgress />
          </Grid>
        )}

        <Grid container spacing={2}>
          <Accordion title="Sistemas">
            {SYSTEM_CONFIGS.map((config) => (
              <Grid
                item
                xs={12}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                key={config.id}
              >
                <Typography variant="body1">{config.label}</Typography>
                <Switch
                  checked={sistemConfig[config.id]}
                  onChange={(e) =>
                    setSistemConfig({
                      ...sistemConfig,
                      [config.id]: e.target.checked,
                    })
                  }
                  name={config.id}
                  disabled={!structureFilters.idLoja}
                />
              </Grid>
            ))}
          </Accordion>

          <Accordion title="Formas de Entrega">
            {SHIPPING_CONFIGS.map((config) => (
              <>
                <Grid
                  item
                  xs={12}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  key={config.id}
                >
                  <div>
                    <Typography variant="body1" style={{ margin: "0" }}>
                      {config.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{ fontSize: "0.65rem" }}
                    >
                      {config.description}
                    </Typography>
                  </div>
                  <Switch
                    checked={shippingConfig[config.id].active}
                    onChange={(e) =>
                      setShippingConfig((prevState) => ({
                        ...prevState,
                        [config.id]: {
                          ...prevState[config.id],
                          active: e.target.checked,
                        },
                      }))
                    }
                    name={config.id}
                    disabled={!structureFilters.idLoja}
                  />
                </Grid>

                {config.child && (
                  <Grid
                    item
                    xs={12}
                    hidden={!shippingConfig[config.id].active}
                    style={{ marginLeft: "1rem" }}
                  >
                    <Grid container spacing={2}>
                      {config.child.map((field) => (
                        <Grid
                          item
                          xs={12}
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          key={field.id}
                        >
                          <Typography variant="body1">{field.label}</Typography>
                          <OutlinedInput
                            type={field.type}
                            inputProps={{ min: 0 }}
                            value={shippingConfig[config.id][field.id]}
                            onChange={(e) => {
                              setShippingConfig((prevState) => {
                                return {
                                  ...prevState,
                                  [config.id]: {
                                    ...prevState[config.id],
                                    [field.id]: e.target.value,
                                  },
                                };
                              });
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}
              </>
            ))}
          </Accordion>

          <Accordion title="Formas de Pagamento">
            {PAYMENT_CONFIGS.map((config) => (
              <>
                <Grid
                  item
                  xs={12}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  key={config.id}
                >
                  <Typography variant="body1" style={{ margin: "0" }}>
                    {config.label}
                  </Typography>
                  <Switch
                    checked={paymentConfig[config.id].active}
                    onChange={(e) =>
                      setPaymentConfig((prevState) => ({
                        ...prevState,
                        [config.id]: {
                          ...prevState[config.id],
                          active: e.target.checked,
                        },
                      }))
                    }
                    name={config.id}
                    disabled={!structureFilters.idLoja}
                  />
                </Grid>

                {config.child && (
                  <Grid
                    item
                    xs={12}
                    hidden={!paymentConfig[config.id].active}
                    style={{ marginLeft: "1rem" }}
                  >
                    <Grid container spacing={2}>
                      {config.child.map((field) => (
                        <Grid
                          item
                          xs={12}
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          key={field.id}
                        >
                          <Typography variant="body1">{field.label}</Typography>
                          <OutlinedInput
                            type={field.type}
                            inputProps={{ min: 0 }}
                            value={paymentConfig[config.id][field.id]}
                            onChange={(e) => {
                              setPaymentConfig((prevState) => {
                                return {
                                  ...prevState,
                                  [config.id]: {
                                    ...prevState[config.id],
                                    [field.id]: e.target.value,
                                  },
                                };
                              });
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}
              </>
            ))}
          </Accordion>

          <Grid item xs={12} alignItems="flex-end">
            <Button
              style={{ marginTop: "1rem", width: "30vw" }}
              variant="contained"
              disabled={
                updatingConfigs ||
                !structureFilters.idLoja ||
                structureFiltersLoading.length > 0
              }
              onClick={() => {
                handleUpdateConfigs();
              }}
            >
              {"Salvar"}
            </Button>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
